// src/pages/CommercialRent.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { PublicAPI } from '../api/public';
import { useAppStore } from '../store/useAppStore';
import { useCurrencyStore } from '../store/useCurrencyStore';
import FilterBar from '../components/FilterBar';
import ListingCard from '../components/ListingCard';

const Container = styled.main`
  width: min(1400px, 96vw);
  margin: 20px auto;
  padding: 0 18px;
`;

const Title = styled.h1`
  margin: 14px 0;
  font-size: clamp(28px, 5vw, 40px);
  font-weight: 800;
`;

const Description = styled.p`
  margin: 0 0 20px 0;
  opacity: 0.8;
  font-size: 18px;
  line-height: 1.6;
`;

const Grid = styled.div`
  display: grid; 
  gap: 20px;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  margin: 20px 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Pager = styled.div`
  display: flex; 
  gap: 10px; 
  justify-content: center; 
  margin: 20px 0 40px;
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  opacity: 0.7;
  
  h3 {
    margin: 0 0 10px 0;
    font-size: 24px;
  }
  
  p {
    margin: 0;
    font-size: 16px;
  }
`;

export default function CommercialRent() {
  const { t } = useTranslation();
  const regionId = useAppStore(s => s.regionId);
  const currency = useCurrencyStore(s => s.currency);
  
  const [items, setItems] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [params, setParams] = React.useState({
    property_type: 'commercial_rent', // Коммерческая аренда
  });

  // Подхватываем фильтры из URL
  React.useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const urlParams = {};
    
    if (sp.get('district_id')) urlParams.district_id = Number(sp.get('district_id'));
    if (sp.get('price_min')) urlParams.price_min = Number(sp.get('price_min'));
    if (sp.get('price_max')) urlParams.price_max = Number(sp.get('price_max'));
    if (sp.get('area_min')) urlParams.area_min = Number(sp.get('area_min'));
    if (sp.get('area_max')) urlParams.area_max = Number(sp.get('area_max'));
    
    if (Object.keys(urlParams).length) {
      setParams(prev => ({ ...prev, ...urlParams }));
    }
  }, []);

  // Загрузка данных
  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      
      try {
        const data = await PublicAPI.properties({
          ...params,
          page,
          page_size: 12,
          region_id: regionId || undefined,
          currency: currency || undefined,
          locale: 'ru',
        });
        
        if (!cancelled) {
          const list = Array.isArray(data?.properties) ? data.properties : [];
          const tot = data?.pagination?.total ?? 0;
          setItems(list);
          setTotal(tot);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Ошибка загрузки');
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [params, page, regionId, currency]);

  const handleSearch = (newParams) => {
    const searchParams = {
      property_type: 'commercial_rent', // Всегда коммерческая аренда
      ...newParams,
    };
    
    setItems([]);
    setPage(1);
    setParams(searchParams);
    
    // Синхронизация с URL
    const usp = new URLSearchParams();
    Object.entries(searchParams).forEach(([k, v]) => {
      if (v !== '' && v != null && k !== 'property_type') {
        usp.set(k, String(v));
      }
    });
    const url = `${window.location.pathname}?${usp.toString()}`;
    window.history.replaceState(null, '', url);
  };

  const totalPages = Math.max(1, Math.ceil((total || 0) / 12));
  const goTo = (n) => {
    setPage(Math.max(1, Math.min(totalPages, n)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Container>
      <Title>{t('nav.commercialRent') || 'Коммерческая аренда'}</Title>
      
      <Description>
        Офисы, торговые помещения, склады и производственные площади 
        в лучших бизнес-локациях с высокой проходимостью и развитой инфраструктурой.
      </Description>

      <FilterBar
        mode="commercialRent"
        onSearch={handleSearch}
        initialParams={params}
      />

      {error && (
        <div style={{ 
          color: '#c62828', 
          background: '#ffebee', 
          padding: '12px 16px', 
          borderRadius: '8px', 
          margin: '16px 0' 
        }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px', 
          opacity: 0.7 
        }}>
          Загрузка объектов...
        </div>
      )}

      {!loading && !items.length && !error && (
        <EmptyState>
          <h3>Объекты не найдены</h3>
          <p>Попробуйте изменить параметры поиска или сбросить фильтры</p>
        </EmptyState>
      )}

      {items.length > 0 && (
        <>
          <Grid>
            {items.map(item => (
              <ListingCard 
                key={item.id ?? item.slug ?? Math.random()} 
                item={item} 
              />
            ))}
          </Grid>

          {totalPages > 1 && (
            <Pager>
              <button 
                className="btn" 
                onClick={() => goTo(page - 1)} 
                disabled={page <= 1}
              >
                ‹ Назад
              </button>
              
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum;
                if (totalPages <= 7) {
                  pageNum = i + 1;
                } else if (page <= 4) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 3) {
                  pageNum = totalPages - 6 + i;
                } else {
                  pageNum = page - 3 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className="btn"
                    data-active={pageNum === page}
                    onClick={() => goTo(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button 
                className="btn" 
                onClick={() => goTo(page + 1)} 
                disabled={page >= totalPages}
              >
                Вперёд ›
              </button>
            </Pager>
          )}
        </>
      )}
    </Container>
  );
}